import React from 'react';
import { METRIC_TO_GRAPH_TYPE } from './performanceConstants';
import { createDateFilter, DATE_REGEX, isWeekOrMonth } from './performanceTabUtil';
import { DashboardContext } from './revenueUpliftContexts';
import { getRanges, utcDate } from './relativeDates';

const DAILY_RE = /^\d{4}-\d{2}-\d{2}$/;
const MONTHLY_RE = /^\d{4}-(0?[1-9]|1[0-2])$/; // YYYY-M or YYYY-MM (1–12)

const ymToParts = key => {
  const [y, m] = key.split('-').map(Number);
  return { y, m: m - 1 }; // 0-based month
};
const partsToYm = (y, m) => `${y}-${String(m + 1).padStart(2, '0')}`;
const nextYm = ({ y, m }) => (m === 11 ? { y: y + 1, m: 0 } : { y, m: m + 1 });

// "YYYY-M" -> "YYYY-MM"
const normalizeMonthKey = k => {
  if (MONTHLY_RE.test(k)) {
    const [y, m] = k.split('-');
    return `${y}-${String(Number(m)).padStart(2, '0')}`;
  }
  return k;
};

/**
 * Accumulates by date, dimension, and type.
 *
 * @param {Array<Object>} data
 *  List of objects containing fields like date_type (e.g., "revenue_2025-07-01", "demand_2025-07-01", …)
 *  and one or more dimension fields named productDimXXXId.
 * @param {Function} [dateFilter] – function that receives the date string ("2025-07-01")
 *  and returns whether to include it.
 * @param {string} dimensionKey – name of the dimension field we want to group by (e.g., "productDim575Id").
 * @returns {Object}
 *   {
 *     "2025-07-01": {
 *       "129018": { revenue: 123.45, demand:  67 },
 *       "129019": { revenue:  45.00, demand:  12 },
 *       …
 *     },
 *     "2025-07-02": { … },
 *     …
 *   }
 */
const accumulateByDateAndDimension = (data, dateFilter = () => true, dimensionKey) => {
  const acc = {};
  const dateRe = new RegExp(DATE_REGEX);

  data.forEach(item => {
    const dimId = item[dimensionKey];
    if (dimId == null) {
      return;
    }

    Object.entries(item).forEach(([key, value]) => {
      const m = key.match(dateRe);
      if (!m) {
        return;
      }
      const [, type, date] = m; // type = "revenue" / "demand" / etc.
      if (!dateFilter(date) || typeof value !== 'number') {
        return;
      }

      acc[date] = acc[date] || {};
      acc[date][dimId] = acc[date][dimId] || {};
      acc[date][dimId][type] = (acc[date][dimId][type] || 0) + value;
    });
  });

  return acc;
};

/**
 * Combines two accumulated data objects (e.g., history + forecast),
 * summing each type for each date and dimension.
 *
 * @param {Object} a - output of accumulateByDateAndDimension
 * @param {Object} b - output of accumulateByDateAndDimension
 * @returns {Object} same format, with type sums = a + b
 */
const combineByDateAndDimension = (a = {}, b = {}) => {
  const dates = new Set([...Object.keys(a), ...Object.keys(b)]);
  const out = {};

  dates.forEach(date => {
    const da = a[date] || {};
    const db = b[date] || {};
    out[date] = {};

    // dimensions that appear in a or b
    const dims = new Set([...Object.keys(da), ...Object.keys(db)]);
    dims.forEach(dimId => {
      const ta = da[dimId] || {};
      const tb = db[dimId] || {};
      out[date][dimId] = {};

      // types that appear in a or b: revenue, demand, …
      const types = new Set([...Object.keys(ta), ...Object.keys(tb)]);
      types.forEach(type => {
        out[date][dimId][type] = (ta[type] || 0) + (tb[type] || 0);
      });
    });
  });

  return out;
};

/**
 * Given an object mapping dates → { id → { revenue, demand, … } },
 * returns a new object mapping each date to its ids.
 *
 * @param {Object} dataByDate – { "YYYY-MM-DD": { "123": { revenue: x, demand: y }, … }, … }
 * @returns {Object} same shape.
 */
const calculateByDate = dataByDate => {
  const result = {};

  const normalized = Object.fromEntries(Object.entries(dataByDate).map(([k, v]) => [normalizeMonthKey(k), v]));
  Object.keys(normalized).forEach(date => {
    const metricsById = normalized[date];

    // Convert to array of rows
    const rows = Object.entries(metricsById).map(([id, metrics]) => ({
      id,
      ...metrics,
    }));

    // Build the output for this date
    result[date] = {};
    rows.forEach(row => {
      const { id, ...metrics } = row;
      result[date][id] = metrics;
    });
  });

  return result;
};

/**
 * Returns all dates between min and max (inclusive), sorted,
 * filling in gaps. Supports daily (YYYY-MM-DD) keys.
 * If endKey is provided (in the same format as the keys), it extends to that end even if data is missing.
 *
 * @param {Array<string>} keys - list of date keys (daily)
 * @param {string} [endKey] - optional end key to extend through (inclusive)
 * @returns {Array<string>} continuous sorted dates
 */
const getContinuousSortedDates = (keys, endKey) => {
  if (!keys?.length) {
    return [];
  }

  // daily
  if (keys.every(k => DAILY_RE.test(k))) {
    const sorted = [...keys].sort((a, b) => utcDate(a) - utcDate(b));
    const start = utcDate(sorted[0]);
    let end = utcDate(sorted[sorted.length - 1]);

    if (endKey && DAILY_RE.test(endKey)) {
      const desiredEnd = utcDate(endKey);
      if (desiredEnd > end) {
        end = desiredEnd;
      }
    }

    const out = [];
    for (
      let d = utcDate(`${start.getUTCFullYear()}-${start.getUTCMonth()}-${start.getUTCDate()}`);
      d <= end;
      d.setUTCDate(d.getUTCDate() + 1)
    ) {
      out.push(d.toISOString().slice(0, 10)); // YYYY-MM-DD
    }
    return out;
  }

  // monthly
  if (keys.every(k => MONTHLY_RE.test(k))) {
    const parts = keys.map(k => ymToParts(normalizeMonthKey(k))).sort((a, b) => a.y - b.y || a.m - b.m);
    let cur = parts[0];
    let last = parts[parts.length - 1];

    if (endKey && MONTHLY_RE.test(endKey)) {
      const desired = ymToParts(endKey);
      if (desired.y > last.y || (desired.y === last.y && desired.m > last.m)) {
        last = desired;
      }
    }

    const out = [];
    while (cur.y < last.y || (cur.y === last.y && cur.m <= last.m)) {
      out.push(partsToYm(cur.y, cur.m));
      cur = nextYm(cur);
    }
    return out;
  }

  return [...keys].sort();
};

/**
 * Builds a running cumulative sum across dates, then applies calculateByDate.
 *
 * @param {Object} dataByDate – same shape as above
 * @param {string} [fillThroughEndKey] - endKey to extend through (inclusive), filling gaps
 * @returns {Object} cumulative sums
 */
const calculateCumulativeByDate = (dataByDate, fillThroughEndKey) => {
  const cumulativeByDate = {};
  const runningTotals = {};

  const normalized = Object.fromEntries(Object.entries(dataByDate).map(([k, v]) => [normalizeMonthKey(k), v]));
  const dates = getContinuousSortedDates(Object.keys(normalized), fillThroughEndKey);
  if (dates.length === 0) {
    return {};
  }

  dates.forEach(date => {
    const today = normalized[date] || {};

    // Update running totals
    Object.entries(today).forEach(([id, metrics]) => {
      if (!runningTotals[id]) {
        runningTotals[id] = {};
      }
      Object.entries(metrics).forEach(([k, v]) => {
        if (runningTotals[id][k] == null) {
          runningTotals[id][k] = 0;
        }
        runningTotals[id][k] += v;
      });
    });

    // Copy into cumulativeByDate
    cumulativeByDate[date] = {};
    Object.entries(runningTotals).forEach(([id, metrics]) => {
      cumulativeByDate[date][id] = { ...metrics };
    });
  });

  return calculateByDate(cumulativeByDate);
};

const getEndKeyForPeriod = (period, endDate) => {
  const isDaily = isWeekOrMonth(period);

  if (isDaily) {
    return `${endDate.getUTCFullYear()}-${String(endDate.getUTCMonth() + 1).padStart(2, '0')}-${endDate.getUTCDate()}`; // YYYY-MM-DD
  }

  return `${endDate.getUTCFullYear()}-${String(endDate.getUTCMonth() + 1).padStart(2, '0')}`; // YYYY-MM
};

const isAtOrBeforeCutoff = (dateString, cutoffDate, period) => {
  const currentDate = utcDate(dateString);
  if (isWeekOrMonth(period)) {
    return currentDate <= cutoffDate;
  }
  const cYear = currentDate.getFullYear();
  const cutoffYear = cutoffDate.getFullYear();
  const cMonth = currentDate.getMonth();
  const cutoffMonth = cutoffDate.getMonth();

  return cYear < cutoffYear || (cYear === cutoffYear && cMonth <= cutoffMonth);
};

const convertToArrayWithActualAndForecast = (
  dataByDate,
  metricKey,
  relativeDates,
  formatNumber,
  period,
  { cumulative, monthlyHistoryRaw, monthlyForecastRaw },
) => {
  const cutoff = relativeDates.yesterday;
  const isMonthly = !isWeekOrMonth(period);
  const cutoffYM = `${cutoff.getUTCFullYear()}-${String(cutoff.getUTCMonth() + 1).padStart(2, '0')}`;
  const histAtCutoffById = isMonthly ? monthlyHistoryRaw?.[cutoffYM] ?? {} : {};

  let baselineById = {};

  if (cumulative) {
    if (isMonthly) {
      const keys = Object.keys(dataByDate)
        .map(normalizeMonthKey)
        .filter(k => MONTHLY_RE.test(k))
        .sort();

      const prevKey = keys.filter(k => k < cutoffYM).at(-1);
      if (prevKey) {
        baselineById = Object.fromEntries(
          Object.entries(dataByDate[prevKey] || {}).map(([id, metrics]) => [id, metrics[metricKey] ?? 0]),
        );
      }
    } else {
      // daily
      const lastKeyUpToYesterday = Object.keys(dataByDate)
        .filter(date => utcDate(date) <= cutoff)
        .sort((a, b) => utcDate(a) - utcDate(b))
        .at(-1);

      if (lastKeyUpToYesterday) {
        baselineById = Object.fromEntries(
          Object.entries(dataByDate[lastKeyUpToYesterday] || {}).map(([id, metrics]) => [id, metrics[metricKey] ?? 0]),
        );
      }
    }
  }

  const out = {};

  Object.entries(dataByDate).forEach(([dateKey, metricsById]) => {
    const onOrBefore = isAtOrBeforeCutoff(dateKey, cutoff, period);

    const rows = Object.entries(metricsById)
      .map(([name, metrics]) => {
        const total = metrics[metricKey] ?? 0;

        const isCurrentMonth =
          isMonthly && MONTHLY_RE.test(normalizeMonthKey(dateKey)) && normalizeMonthKey(dateKey) === cutoffYM;

        if (!cumulative) {
          if (isMonthly && isCurrentMonth) {
            const histMonth = monthlyHistoryRaw?.[dateKey]?.[name]?.[metricKey] ?? 0;
            const fcMonth = monthlyForecastRaw?.[dateKey]?.[name]?.[metricKey] ?? 0;
            const current = histMonth;
            const forecast = fcMonth;
            return {
              name,
              current,
              forecast,
              value: current + forecast,
              currentTooltip: `Actuals: ${formatNumber(current)}`,
              forecastTooltip: `Forecast: ${formatNumber(forecast)}`,
            };
          }

          const current = onOrBefore ? total : 0;
          const forecast = onOrBefore ? 0 : total;
          return {
            name,
            current,
            forecast,
            value: current + forecast,
            currentTooltip: `Actuals: ${formatNumber(current)}`,
            forecastTooltip: `Forecast: ${formatNumber(forecast)}`,
          };
        }

        // cumulative
        const base = baselineById[name] ?? 0;

        if (isMonthly && isCurrentMonth) {
          const histMonth = monthlyHistoryRaw?.[dateKey]?.[name]?.[metricKey] ?? 0;
          const fcMonth = monthlyForecastRaw?.[dateKey]?.[name]?.[metricKey] ?? 0;
          const current = base + histMonth;
          const forecast = fcMonth;
          return {
            name,
            current,
            forecast,
            value: current + forecast,
            currentTooltip: `Actuals: ${formatNumber(current)}`,
            forecastTooltip: `Forecast: ${formatNumber(forecast)}`,
          };
        }

        if (onOrBefore) {
          const current = total;
          const forecast = 0;
          return {
            name,
            current,
            forecast,
            value: total,
            currentTooltip: `Actuals: ${formatNumber(current)}`,
            forecastTooltip: `Forecast: ${formatNumber(forecast)}`,
          };
        }

        const histAtCutoff = isMonthly ? histAtCutoffById?.[name]?.[metricKey] ?? 0 : 0;
        const carry = base + histAtCutoff;
        const forecast = Math.max(0, total - carry);

        return {
          name,
          current: carry,
          forecast,
          value: carry + forecast,
          currentTooltip: `Actuals: ${formatNumber(carry)}`,
          forecastTooltip: `Forecast: ${formatNumber(forecast)}`,
        };
      })
      .sort((a, b) => (b.value ?? 0) - (a.value ?? 0));

    out[dateKey] = rows;
  });

  return out;
};

const getBreakdownData = (
  period,
  metric,
  dimensionId,
  dailyHistory,
  dailyForecast,
  monthlyHistory,
  monthlyForecast,
  rangesApi,
  cumulative,
  relativeDates,
  graphNumberFormatter,
) => {
  const { currentRange, lastRange, stlyRange } = rangesApi;

  // For current period, combine forecast and history; for others, use history only
  const currentHistoryData = isWeekOrMonth(period) ? dailyHistory : monthlyHistory;
  const currentForecastData = isWeekOrMonth(period) ? dailyForecast : monthlyForecast;
  const lastData = isWeekOrMonth(period) ? dailyHistory : monthlyHistory;
  const stlyData = isWeekOrMonth(period) ? dailyHistory : monthlyHistory;

  const dimKey = `productDim${dimensionId}Id`;

  const currentDateFilter = createDateFilter(currentRange.start, currentRange.end);
  const currentHistoryAccumulated = accumulateByDateAndDimension(currentHistoryData, currentDateFilter, dimKey);
  const currentForecastAccumulated = accumulateByDateAndDimension(currentForecastData, currentDateFilter, dimKey);

  // combine history and forecast for each dimension
  const currentAccumulated = combineByDateAndDimension(currentHistoryAccumulated, currentForecastAccumulated);

  const lastAccumulated = accumulateByDateAndDimension(
    lastData,
    createDateFilter(lastRange.start, lastRange.end),
    dimKey,
  );
  const stlyAccumulated = stlyRange
    ? accumulateByDateAndDimension(stlyData, createDateFilter(stlyRange.start, stlyRange.end), dimKey)
    : {};

  const currentMonthlyHistoryRaw = calculateByDate(currentHistoryAccumulated);
  const currentMonthlyForecastRaw = calculateByDate(currentForecastAccumulated);
  const currentTopN = cumulative
    ? calculateCumulativeByDate(currentAccumulated, getEndKeyForPeriod(period, currentRange.end))
    : calculateByDate(currentAccumulated);

  const lastTopN = cumulative
    ? calculateCumulativeByDate(lastAccumulated, getEndKeyForPeriod(period, lastRange.end))
    : calculateByDate(lastAccumulated);
  const stlyTopN = stlyRange
    ? cumulative
      ? calculateCumulativeByDate(stlyAccumulated, getEndKeyForPeriod(period, stlyRange.end))
      : calculateByDate(stlyAccumulated)
    : {};

  const graphType = METRIC_TO_GRAPH_TYPE[metric];

  const currentTopBreakdown = convertToArrayWithActualAndForecast(
    currentTopN,
    graphType,
    relativeDates,
    graphNumberFormatter,
    period,
    { cumulative, monthlyHistoryRaw: currentMonthlyHistoryRaw, monthlyForecastRaw: currentMonthlyForecastRaw },
  );
  const lastTopBreakdown = convertToArrayWithActualAndForecast(
    lastTopN,
    graphType,
    relativeDates,
    graphNumberFormatter,
    period,
    { cumulative },
  );
  const stlyTopBreakdown = stlyRange
    ? convertToArrayWithActualAndForecast(stlyTopN, graphType, relativeDates, graphNumberFormatter, period, {
        cumulative,
      })
    : {};

  return {
    currentTopBreakdown,
    lastTopBreakdown,
    stlyTopBreakdown,
  };
};

const usePerformanceBreakdownData = (
  selectedPeriod,
  selectedMetric,
  selectedDimensionId,
  relativeDates,
  periodScope,
  cumulativeTotals,
  graphNumberFormatter,
) => {
  const {
    dailyPerformanceForecast,
    monthlyPerformanceForecast,
    dailyPerformanceHistory,
    monthlyPerformanceHistory,
  } = React.useContext(DashboardContext);

  const rangesApi = React.useMemo(() => getRanges(selectedPeriod, relativeDates, periodScope), [
    periodScope,
    relativeDates,
    selectedPeriod,
  ]);

  const data = React.useMemo(
    () =>
      getBreakdownData(
        selectedPeriod,
        selectedMetric,
        selectedDimensionId,
        dailyPerformanceHistory,
        dailyPerformanceForecast,
        monthlyPerformanceHistory,
        monthlyPerformanceForecast,
        rangesApi,
        cumulativeTotals,
        relativeDates,
        graphNumberFormatter,
      ),
    [
      cumulativeTotals,
      dailyPerformanceForecast,
      dailyPerformanceHistory,
      graphNumberFormatter,
      monthlyPerformanceForecast,
      monthlyPerformanceHistory,
      rangesApi,
      relativeDates,
      selectedDimensionId,
      selectedMetric,
      selectedPeriod,
    ],
  );

  return data;
};

export default usePerformanceBreakdownData;
